import { supabase } from "@helpers/supabase"
import * as Sentry from "@sentry/react"
import { getUserCountryCode } from "./sessionStorage"

class CLVAPIError extends Error {
  originalError?: unknown

  constructor(originalError?: unknown) {
    super("Failed to fetch CLV, falling back to 0 EUR!")
    this.name = "CLVAPIError"
    this.originalError = originalError
  }
}

export default async function getClvCurrencyAndValue({
  age,
  currency,
  os,
  stripeFirstPriceId,
  stripeNextPriceId,
}: {
  age?: string
  currency: string
  os?: string
  stripeFirstPriceId: string
  stripeNextPriceId: string
}) {
  const appId = "filterly"
  const countryCode = await getUserCountryCode()
  const currencyCode = currency.toUpperCase()
  const firstPriceId = stripeFirstPriceId
  const nextPriceId = stripeNextPriceId
  const productId = import.meta.env.VITE_STRIPE_PRODUCT_ID as string

  try {
    const response = await supabase.functions.invoke("get-clv", {
      body: {
        age,
        appId,
        countryCode,
        currencyCode,
        firstPriceId,
        nextPriceId,
        os,
        productId,
      },
    })

    const data = response.data as { currency: string; value: number }
    const error = response.error as Error

    if (error) throw error

    const clvCurrency = data.currency
    const clvValue = data.value

    if (typeof clvValue !== "number" || typeof clvCurrency !== "string") {
      throw new Error(
        `CLV currency (${clvCurrency}) or value (${clvValue}) is missing!`,
      )
    }

    return {
      clvCurrency,
      clvValue,
    }
  } catch (error) {
    const clvApiError = new CLVAPIError(error)

    console.error(clvApiError)

    Sentry.captureException(clvApiError, {
      extra: {
        age,
        appId,
        countryCode,
        currency,
        firstPriceId,
        nextPriceId,
        originalError: error,
        os,
        productId,
      },
    })

    return {
      clvCurrency: "EUR",
      clvValue: 0,
    }
  }
}
